import { setProfileDataThunk } from '../../../../store/signUp/thunks';
import { useSelector, useDispatch } from '../../../../store/store';
import { uploadFileThunk } from '../../../../store/general/thunks';
import MotionWrapper from '../../../../containers/MotionWrapper';
import { SignUpSupplementaryStep } from '../../../../store/signUp/types';
import { EditIcon } from '../../../../assets/icons/common';
import CheckBox from '../../../../components/CheckBox';
import Button from '../../../../components/Button';
import styles from './style.module.scss';
import { useState } from 'react';
import {
    setEditMode,
    setSignUpStep,
    setFetchStatus,
} from '../../../../store/signUp/slice';
import {
    selectUserId,
    selectIDPhoto,
    selectExperties,
    selectFetchStatus,
    selectPersonalInfo,
    selectPersonalPhoto,
    selectEducationInfo,
    selectWorkExperience,
    selectVisaAndSalaryRange,
} from '../../../../store/signUp/selectors';

interface Props {
    resumeFile: File | null;
    portfolioFile: File | null;
    coverLetterFile: File | null;
    certificateFile: File | null;
};

function ReviewInfo(props: Props) {
    const {
        resumeFile,
        portfolioFile,
        coverLetterFile,
        certificateFile,
    } = props;

    const dispatch = useDispatch();
    const [agree, setAgree] = useState(false);
    const userId = useSelector(selectUserId);
    const fetchStatus = useSelector(selectFetchStatus);
    const personalInfo = useSelector(selectPersonalInfo);
    const educationInfo = useSelector(selectEducationInfo);
    const workExperience = useSelector(selectWorkExperience);
    const personalPhoto = useSelector(selectPersonalPhoto);
    const IDPhoto = useSelector(selectIDPhoto);
    const experties = useSelector(selectExperties);
    const visaAndSalary = useSelector(selectVisaAndSalaryRange);

    const personalInfoEnteries = personalInfo ? Object.entries(personalInfo) : [];

    const editInfo = (step: SignUpSupplementaryStep) => {
        if (fetchStatus === 'Pending') return;
        dispatch(setEditMode(true));
        dispatch(setSignUpStep(step));
    };

    const submit = async () => {
        if (!userId) return;

        dispatch(setFetchStatus('Pending'));
        resumeFile && await dispatch(uploadFileThunk({ fileName: `${userId}/${resumeFile.name}`, file: resumeFile }));
        portfolioFile && await dispatch(uploadFileThunk({ fileName: `${userId}/${portfolioFile.name}`, file: portfolioFile }));
        coverLetterFile && await dispatch(uploadFileThunk({ fileName: `${userId}/${coverLetterFile.name}`, file: coverLetterFile }));
        certificateFile && await dispatch(uploadFileThunk({ fileName: `${userId}/${certificateFile.name}`, file: certificateFile }));

        const filteredEducationInfo = educationInfo.map(item => {
            const { didNotFinishDegree, ...rest } = item;
            return rest;
        });

        const filteredWorkExperience = workExperience.map(item => {
            const { currentlyworkHere, ...rest } = item;
            return rest;
        });

        const body = {
            experties,
            id: userId,
            race: null,
            gender: null,
            disability: null,
            veteranStatus: null,
            educationInfo: filteredEducationInfo,
            resumeFileKey: resumeFile?.name || '',
            workExperience: filteredWorkExperience,
            portfolioFileKey: portfolioFile?.name || '',
            coverLetterFileKey: coverLetterFile?.name || '',
            certificateFileKey: certificateFile?.name || '',
            desiredAnnualSalaryRange: visaAndSalary?.salaryRange || '',
            requireSponsorshipVisa: visaAndSalary?.requireSponsorshipVisa,
        };

        dispatch(setProfileDataThunk(body))
    };

    return (
        <MotionWrapper className={styles.wrapper}>
            <span className={styles.description}>
                Please confirm the information that you have entered:
            </span>
            <div className={styles.section}>
                <div className={styles.titleBar}>
                    <span>Personal information</span>
                    <EditIcon onClick={() => editInfo('personalInfo')} />
                </div>
                <div className={styles.info_summary}>
                    {personalInfoEnteries.map(item => (<div className={styles.info_summary_item}>
                        <span>{item[0].replace(/_/g, " ")}</span>
                        <span>{item[1] || '------'}</span>
                    </div>))}
                </div>
            </div>

            <div className={styles.section}>
                <div className={styles.titleBar}>
                    <span>Work experience</span>
                    <EditIcon onClick={() => editInfo('workExperience')} />
                </div>
                <div className={styles.info_summary}>
                    {workExperience.length === 0 && <span>No work experience</span>}
                    {workExperience.map((item, indx) => (
                        <>
                            <div className={styles.info_summary_item}>
                                <span>Job title</span>
                                <span>{item.jobTitle}</span>
                            </div>
                            <div className={styles.info_summary_item}>
                                <span>Company</span>
                                <span>{item.company}</span>
                            </div>
                            <div className={styles.info_summary_item}>
                                <span>Location</span>
                                <span>{item.location}</span>
                            </div>
                            <div className={styles.info_summary_item}>
                                <span>Date</span>
                                <span>{item.from}-{item.to || 'Present'}</span>
                            </div>
                            <div className={styles.info_summary_item}>
                                <span>Role description</span>
                                <span>{item.roleDescription}</span>
                            </div>
                            {workExperience.length > indx + 1 && (<div className={styles.divider} />)}
                        </>
                    ))}
                </div>
            </div>

            <div className={styles.section}>
                <div className={styles.titleBar}>
                    <span>Education</span>
                    <EditIcon onClick={() => editInfo('education')} />
                </div>
                <div className={styles.info_summary}>
                    {educationInfo.length === 0 && <span>No education</span>}
                    {educationInfo.map((item, indx) => (
                        <>
                            <div className={styles.info_summary_item}>
                                <span>School or university</span>
                                <span>{item.universityName}</span>
                            </div>
                            <div className={styles.info_summary_item}>
                                <span>Degree</span>
                                <span>{item.degree}</span>
                            </div>
                            <div className={styles.info_summary_item}>
                                <span>Field of study</span>
                                <span>{item.field}</span>
                            </div>
                            <div className={styles.info_summary_item}>
                                <span>Overal result (GPA)</span>
                                <span>{item.GPA}</span>
                            </div>
                            <div className={styles.info_summary_item}>
                                <span>Date</span>
                                <span>{item.from}-{item.to || 'Present'}</span>
                            </div>
                            {educationInfo.length > indx + 1 && (<div className={styles.divider} />)}
                        </>
                    ))}
                </div>
            </div>

            <div className={styles.section}>
                <div className={styles.titleBar}>
                    <span>Expertise</span>
                    <EditIcon onClick={() => editInfo('expertise')} />
                </div>
                <div className={styles.info_summary}>
                    {experties.map(item => {
                        return (<div className={styles.expert}>
                            <span className={styles.expert_title}>
                                {item.expert.title}
                            </span>
                            <div className={styles.skills}>
                                {item.skills.map(skill => <span>{skill.title}</span>)}
                            </div>
                        </div>)
                    })}
                </div>
            </div>

            <div className={styles.section}>
                <div className={styles.titleBar}>
                    <span>Uploaded files</span>
                    <EditIcon onClick={() => editInfo('uploadFiles')} />
                </div>
                <div className={styles.info_summary}>
                    <div className={styles.info_summary_item}>
                        <span>Resume</span>
                        <span>{resumeFile?.name || '------'}</span>
                    </div>
                    <div className={styles.info_summary_item}>
                        <span>Portfolio</span>
                        <span>{portfolioFile?.name || '------'}</span>
                    </div>
                    <div className={styles.info_summary_item}>
                        <span>Cover letter</span>
                        <span>{coverLetterFile?.name || '------'}</span>
                    </div>
                    <div className={styles.info_summary_item}>
                        <span>Certificate</span>
                        <span>{certificateFile?.name || '------'}</span>
                    </div>
                    <div className={styles.info_summary_item}>
                        <span>Salary expectation</span>
                        <span>{visaAndSalary?.salaryRange || '------'}</span>
                    </div>
                </div>
            </div>

            <div className={styles.section}>
                <div className={styles.titleBar}>
                    <span>Personal photo</span>
                    <EditIcon onClick={() => editInfo('uploadProfilePhoto')} />
                </div>
                <div className={styles.info_document}>
                    {personalPhoto && (<img src={personalPhoto} />)}
                </div>
            </div>
            <div className={styles.section}>
                <div className={styles.titleBar}>
                    <span>ID photo</span>
                    <EditIcon onClick={() => editInfo('uploadPersonalID')} />
                </div>
                <div className={styles.info_document}>
                    {IDPhoto && (<img src={IDPhoto} />)}
                </div>
            </div>
            <div className={styles.agreement}>
                <CheckBox
                    text='By clicking here your are indicating 
                that you have read and entered your information correctly.'
                    checked={agree}
                    setChecked={setAgree}
                />
            </div>
            <div className={styles.actions}>
                <Button
                    size='sm'
                    text='Back'
                    type='Outline-Dark'
                    className={styles.actions_btn}
                    disabled={fetchStatus === 'Pending'}
                    onClick={() => editInfo('uploadFiles')}
                />
                <Button
                    size='sm'
                    type='Primary'
                    text='Confirm'
                    onClick={submit}
                    disabled={!agree}
                    className={styles.actions_btn}
                    loading={fetchStatus === 'Pending'}
                />
            </div>
        </MotionWrapper>
    );
};

export default ReviewInfo;
